import * as React from 'react'

import { ChevronDownIcon } from 'lucide-react'
import { MobXProviderContext } from 'mobx-react'

import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  useComboboxAnchor,
} from '~/client/src/shared/components/shadcn-ui/combobox'
import ProjectDateStore, {
  MINUTES_IN_DAY,
} from '~/client/src/shared/stores/ui/ProjectDate.store'
import { doesWindowEndNextDay } from '~/client/src/shared/utils/projectSchedule'

import NextDayLabel from './NextDayLabel'
import { parseTimeInput } from './parseTimeInput'

const TIME_STEP_MINUTES = 30
const SLOTS_IN_DAY = MINUTES_IN_DAY / TIME_STEP_MINUTES

/** A day's 30-minute slots, beginning at `startMinutes` and wrapping midnight. */
function getTimeOptionMinutes(startMinutes = 0): number[] {
  return Array.from(
    { length: SLOTS_IN_DAY },
    (_, index) => (startMinutes + index * TIME_STEP_MINUTES) % MINUTES_IN_DAY,
  )
}

function formatTimeOption(
  projectDateStore: ProjectDateStore,
  minutes: number,
): string {
  const date = projectDateStore.setHours(
    new Date(),
    Math.floor(minutes / 60),
    minutes % 60,
  )
  return projectDateStore.getTimeToDisplay(date)
}

interface IProps {
  value: number
  onChange: (minutes: number) => void
  projectDateStore: ProjectDateStore
  ariaLabel?: string
  /**
   * Offers the options as times *after* this one, wrapping past midnight and
   * marking everything beyond it "next day". Set on an end time so the choice
   * reads as a window from the start rather than a bare clock face; leave it
   * off and the field lists a plain 00:00–23:30 day.
   */
  fromMinutes?: number
  /** Portal target for the options popup; defaults to `document.body` when omitted. */
  portalContainer?: React.ComponentProps<
    typeof ComboboxContent
  >['portalContainer']
}

function TimeCombobox({
  value,
  onChange,
  projectDateStore,
  ariaLabel,
  fromMinutes,
  portalContainer,
}: IProps) {
  // On the mobile build the field has an on-screen keyboard, and the options list
  // and that keyboard must never be open together (both on screen at once
  // mispositions the bottom-anchored sheet). So on mobile, tapping the field opens
  // the keyboard only (the list does not auto-open), and the list is reached via a
  // dedicated chevron that first blurs the field to dismiss the keyboard. Desktop —
  // including the desktop resource card's sheet layout — keeps the default
  // type-and-list combobox with a physical keyboard. Gated on the injected env, not
  // the sheet layout, since that layout is shared across desktop and mobile.
  const { isMobileEnv } = React.useContext(MobXProviderContext) as {
    isMobileEnv?: boolean
  }
  const isMobile = !!isMobileEnv

  // An end time starts one step after the start and runs a full day forward, so
  // it reads as a duration rather than a position in an abstract day; the last
  // slot lands back on the start, which is how a 24-hour window is expressed.
  const options = React.useMemo(
    () =>
      getTimeOptionMinutes(
        fromMinutes == null ? 0 : fromMinutes + TIME_STEP_MINUTES,
      ),
    [fromMinutes],
  )

  const isNextDay = (minutes: number) =>
    fromMinutes != null && doesWindowEndNextDay(fromMinutes, minutes)

  const anchorRef = useComboboxAnchor()
  const isEditingRef = React.useRef(false)
  // Controlled open only on mobile, to keep the list and keyboard mutually
  // exclusive; desktop stays uncontrolled (open === undefined).
  const [open, setOpen] = React.useState(false)

  const openList = () => {
    // Blur the focused field first so the keyboard is dismissed before the list
    // appears — the two are never on screen at the same time on mobile.
    ;(document.activeElement as HTMLElement | null)?.blur()
    setOpen(prev => !prev)
  }

  const formatted = formatTimeOption(projectDateStore, value)
  const [draft, setDraft] = React.useState(formatted)

  React.useEffect(() => {
    if (!isEditingRef.current) {
      setDraft(formatted)
    }
  }, [formatted])

  const commit = (minutes: number | null) => {
    if (minutes != null) {
      onChange(minutes)
      setDraft(formatTimeOption(projectDateStore, minutes))
    } else {
      setDraft(formatted)
    }
  }

  const commitDraft = () => commit(parseTimeInput(draft))

  return (
    <Combobox
      items={options}
      value={value}
      itemToStringLabel={minutes => formatTimeOption(projectDateStore, minutes)}
      onValueChange={minutes => {
        if (minutes != null) commit(minutes)
      }}
      inputValue={draft}
      onInputValueChange={setDraft}
      filter={null}
      open={isMobile ? open : undefined}
      // On mobile, honor only close requests (item pick, outside tap, escape).
      // Base UI otherwise opens the list on tap and on typing, which would put it
      // on screen next to the keyboard; the list opens exclusively via the chevron
      // (direct setOpen), so it never shares the screen with the keyboard.
      onOpenChange={
        isMobile
          ? nextOpen => {
              if (!nextOpen) setOpen(false)
            }
          : undefined
      }
      openOnInputClick={isMobile ? false : undefined}
    >
      <div ref={anchorRef} className="tw:relative">
        {/* Sits over the field rather than in its text: the input stays
            parseable, and the marker is chrome, not something to edit. An
            invisible copy of the value reserves exactly the width the real text
            occupies, so the marker follows it instead of being pinned to the
            right edge — and it keeps up when the time format changes width. */}
        {isNextDay(value) && (
          <span className="tw:pointer-events-none tw:absolute tw:inset-y-0 tw:left-0 tw:z-10 tw:flex tw:items-center tw:pl-2.5 tw:text-base tw:md:text-sm">
            <span className="tw:invisible" aria-hidden="true">
              {draft}
            </span>
            <NextDayLabel />
          </span>
        )}
        <ComboboxInput
          aria-label={ariaLabel}
          className="tw:w-full"
          showTrigger={!isMobile}
          onFocus={() => {
            isEditingRef.current = true
            // Typing and the list are mutually exclusive on mobile: focusing the
            // field to type closes the list.
            if (isMobile) setOpen(false)
          }}
          onBlur={() => {
            isEditingRef.current = false
            commitDraft()
          }}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault()
              commitDraft()
            }
          }}
        />
        {isMobile && (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={ariaLabel}
            // Full field height and a wider strip give the toggle a comfortable
            // touch target; the chevron itself stays 16px.
            className="tw:absolute tw:inset-y-0 tw:right-0 tw:h-full tw:w-10 tw:rounded-l-none"
            onClick={openList}
          >
            <ChevronDownIcon className="tw:size-4 tw:text-muted-foreground" />
          </Button>
        )}
      </div>
      <ComboboxContent
        anchor={anchorRef}
        portalContainer={portalContainer}
        className="tw:pointer-events-auto"
      >
        <ComboboxList>
          {(minutes: number) => (
            <ComboboxItem
              key={minutes}
              value={minutes}
              // tabular-nums keeps the markers in a column: every time in a
              // list is the same length, so equal-width digits make every row
              // the same width. gap-0 overrides the item's own gap — the marker
              // brings its own spacing, and both together made the list's gap
              // twice the field's. The checkmark is absolutely positioned, so
              // it is unaffected.
              className="tw:gap-0 tw:tabular-nums tw:whitespace-nowrap"
            >
              {formatTimeOption(projectDateStore, minutes)}
              {isNextDay(minutes) && <NextDayLabel />}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}

export default TimeCombobox
